import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Loader2 } from 'lucide-react';
import { useLang } from '../i18n';
import { useSEO } from '../seo';
import { PageHeader } from '../components/PageHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LegalPage({ docType, titleKey, testid }) {
  const { t, lang } = useLang();
  const locale = { pt: 'pt-PT', en: 'en-GB', de: 'de-DE', fr: 'fr-FR' }[lang] || 'pt-PT';
  const [doc, setDoc] = useState(null);
  const title = t(titleKey);
  useSEO(`${title} | Eco Collection`, title);

  useEffect(() => {
    setDoc(null);
    fetch(`${API}/legal/${docType}?lang=${lang}`)
      .then((r) => (r.ok ? r.json() : { status: 'in_preparation' }))
      .then((d) => setDoc(d))
      .catch(() => setDoc({ status: 'in_preparation' }));
  }, [docType, lang]);

  const published = doc?.status === 'published';
  const updatedLabel = useMemo(() => {
    if (!doc?.published_at) return '';
    try {
      return new Date(doc.published_at).toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });
    } catch {
      return '';
    }
  }, [doc, locale]);

  return (
    <div data-testid={`${testid}-page`}>
      <PageHeader kicker="Eco Collection" title={title} sub={published ? `${t('legal.version')} ${doc.version} · ${t('legal.updated')} ${updatedLabel}` : ''} />
      <section className="bg-eco-cream py-12">
        <div className="eco-container max-w-3xl">
          {!doc ? (
            <div className="grid place-items-center py-10"><Loader2 className="animate-spin text-eco-green" size={26} /></div>
          ) : !published ? (
            <div data-testid={`${testid}-preparation`} className="rounded-2xl border border-dashed border-eco-ink/15 bg-white p-10 text-center">
              <span className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-eco-cream text-eco-green"><FileText size={20} /></span>
              <h2 className="mt-4 text-base font-semibold text-eco-ink md:text-lg">{t('legal.prep.t')}</h2>
              <p className="eco-secondary mx-auto mt-2 max-w-md text-eco-ink/60">{t('legal.prep.s')}</p>
              <Link to="/contact" data-testid={`${testid}-prep-contact`} className="eco-btn-secondary mt-5 inline-flex">{t('foot.sup.contact')}</Link>
            </div>
          ) : (
            <div className="grid gap-6 lg:grid-cols-[220px_1fr]">
              <nav data-testid={`${testid}-toc`} className="h-fit rounded-2xl border border-eco-ink/10 bg-white p-5 lg:sticky lg:top-24">
                <p className="eco-field-label">{t('legal.toc')}</p>
                <ul className="mt-3 space-y-2">
                  {doc.sections.map((s) => (
                    <li key={s.id}>
                      <a href={`#legal-${s.id}`} data-testid={`${testid}-toc-${s.id}`} className="block text-xs text-eco-ink/60 transition-colors hover:text-eco-green">{s.title}</a>
                    </li>
                  ))}
                </ul>
              </nav>
              <article data-testid={`${testid}-content`} className="rounded-2xl border border-eco-ink/10 bg-white p-6 md:p-8">
                {doc.sections.map((s) => (
                  <section key={s.id} id={`legal-${s.id}`} data-testid={`${testid}-section-${s.id}`} className="mb-7 scroll-mt-24 last:mb-0">
                    <h2 className="text-base font-semibold text-eco-ink md:text-lg">{s.title}</h2>
                    {s.body.split('\n\n').map((p, i) => (
                      <p key={i} className="mt-2.5 text-sm leading-relaxed whitespace-pre-line text-eco-ink/70">{p}</p>
                    ))}
                  </section>
                ))}
                <div className="mt-8 border-t border-eco-ink/10 pt-5 text-xs text-eco-ink/45">
                  {t('legal.version')} {doc.version} · {t('legal.updated')} {updatedLabel}
                </div>
              </article>
            </div>
          )}
          <Link to="/" data-testid={`${testid}-back`} className="eco-btn-text mt-8 inline-flex"><ArrowLeft size={14} /> {t('legal.back')}</Link>
        </div>
      </section>
    </div>
  );
}
